/*
 * Copyright 2017-present Evernote Corporation. All rights reserved.
 */

export class Node<T> {
  value: T;
  next: Node<T> | null;
  prev: Node<T> | null;

  constructor(value: T) {
    this.value = value;
    this.next = null;
    this.prev = null;
  }
}

interface NodeLocationResult<T> {
  index: number;
  node: Node<T> | null;
  err?: string;
}

export default class DoublyLinkedList<T> {
  length: number;
  head: Node<T> | null;
  tail: Node<T> | null;

  constructor() {
    this.length = 0;
    this.head = null;
    this.tail = null;
  }

  clear() {
    this.length = 0;
    this.head = null;
    this.tail = null;
  }

  push(value: T) {
    if (this.length === 0) {
      const node = new Node(value);
      this.head = node;
      this.tail = node;
      this.length++;
      return;
    }

    // null ptr check
    if (!this.tail) {
      throw new Error('cannot push: tail is null');
    }

    this.tail.next = new Node(value);
    this.tail.next.prev = this.tail;
    this.tail = this.tail.next;
    this.length++;
  }

  pop(): T {
    if (this.length === 0) {
      throw new Error('cannot pop: list is empty');
    }

    if (this.length === 1 && this.head) {
      this.length = 0;
      const n = this.head;
      this.head = null;
      this.tail = null;
      return n.value;
    }

    // null ptr check before pop
    if (this.tail == null || this.tail.prev == null) {
      throw new Error('cannot pop: tail and/or second to last nodes were null');
    }

    const n = this.tail;
    this.tail = this.tail.prev; // Slide tail back one
    n.prev = null;
    this.tail.next = null;
    this.length--;
    return n.value;
  }

  insert(index: number, value: T) {
    // Special case: Target is the first node in the list
    if (index === 0) {
      return this.unshift(value);
    }

    // Special case: Target is the last node in the list
    if (index === this.length - 1) {
      return this.push(value);
    }

    const target = this._findTargetNodeByIndex(index - 1);

    if (!target || !target.next) {
      throw new Error('cannot insert: target index is last element in list');
    }

    const n = new Node(value);
    const after = target.next;
    n.next = after;
    n.prev = target;
    after.prev = n;
    target.next = n;
    this.length++;
  }

  unshift(value: T) {
    // Special case: List is currently empty
    if (this.length === 0 || this.head == null) {
      return this.push(value);
    }

    const n = new Node(value);
    const currentHead = this.head;
    n.next = currentHead;
    currentHead.prev = n;
    this.head = n;
    this.length++;
  }

  shift(): T | null {
    if (this.length === 0 || this.head == null) {
      throw new Error('cannot shift: list is empty');
    }

    if (this.length === 1 || this.head.next == null) {
      return this.pop();
    }

    const n = this.head;
    const newHead = this.head.next;
    newHead.prev = null;
    this.head = newHead;
    n.next = null;
    this.length--;
    return n.value;
  }

  slice(start: number, numberToRemove: number = 1): void {
    if (this.length === 0) {
      throw new Error('cannot slice: list is empty');
    }

    // Special case where starting node is last node in list
    if (start === this.length - 1) {
      // If the starting node is the last node, we know we can only remove that
      // very last node
      this.pop();
      return;
    }

    // Don't allow numberToRemove to go beyond the end of the list
    const howMany =
      start + numberToRemove > this.length ? this.length - start : numberToRemove;

    // Special cases where the starting node is the first node in list
    if (start === 0) {
      if (howMany === this.length) {
        // Same as a clear request
        this.clear();
        return;
      }

      // If start is 0 but we aren't clearing the list, find the end node and
      // just move the head of the list to match
      this.head = this._findTargetNodeByIndex(howMany);

      if (this.head && this.head.prev) {
        this.head.prev.next = null;
        this.head.prev = null;
      }

      this.length -= howMany;
      return;
    }

    // What if the last node in the removal zone is the final node of the list?
    if (start + numberToRemove === this.length) {
      // We can just move the tail to before the removal zone
      const startingNode = this._findTargetNodeByIndex(start);
      if (!startingNode || !startingNode.prev) {
        throw new Error('cannot slice: unknown error');
      }

      this.tail = startingNode.prev;
      this.tail.next = null;
      startingNode.prev = null;

      this.length -= numberToRemove;
      return;
    }

    // Finally, the general case:
    const startingNode = this._findTargetNodeByIndex(start);
    if (startingNode == null) {
      throw new Error('cannot slice: startingNode was null');
    }

    let finalNode = startingNode;
    for (let i = 0; i < numberToRemove; i++) {
      if (finalNode.next == null) {
        throw new Error('cannot slice: finalNode.next was null');
      }

      finalNode = finalNode.next;
    }

    // null ptr check: this state should be impossible since we've dealt with
    // the edge cases where the starting node is the first element of the list
    // and where the final node is the last element of the list
    if (startingNode.prev == null || finalNode.next == null) {
      throw new Error('cannot slice: unknown error');
    }

    // Grab the nodes before and after the removal zone
    const beforeRemovalZone = startingNode.prev;
    const afterRemovalZone = finalNode.next;
    // Disconnect the range of nodes to be removed from rest of list
    startingNode.prev = null;
    finalNode.next = null;

    // Connect the nodes before and after the removal zone
    beforeRemovalZone.next = afterRemovalZone;
    afterRemovalZone.prev = beforeRemovalZone;

    // Diminish the length of the list
    this.length -= numberToRemove;
  }

  sliceNode(target: Node<T>) {
    if (target === this.head) {
      return this.shift();
    }

    if (target === this.tail) {
      return this.pop();
    }

    const beforeTarget = target.prev;
    const afterTarget = target.next;

    // null ptr check
    if (beforeTarget && afterTarget) {
      beforeTarget.next = afterTarget;
      afterTarget.prev = beforeTarget;
    }

    target.next = null;
    target.prev = null;
    this.length--;
    return target.value;
  }

  get(index: number): T {
    const target = this._findTargetNodeByIndex(index);

    if (!target) {
      throw new Error('cannot get: retrieved node was null');
    }

    return target.value;
  }

  remove(index: number): T {
    const target = this._findTargetNodeByIndex(index);

    if (!target) {
      throw new Error('cannot remove: retrieved node was null');
    }

    this.sliceNode(target);
    return target.value;
  }

  first(): T | null {
    return this.head ? this.head.value : null;
  }

  last(): T | null {
    return this.tail ? this.tail.value : null;
  }

  find(value: T): NodeLocationResult<T> {
    return this._findTargetNodeByValue(value);
  }

  forEach(iter: (value: T, index: number) => void): void {
    if (this.length === 0) {
      return;
    }

    let current = this.head;
    for (let i = 0; current != null; i++) {
      iter(current.value, i);
      current = current.next;
    }
  }

  forEachReverse(iter: (value: T, index: number) => void): void {
    if (this.length === 0) {
      return;
    }

    let current = this.tail;
    for (let i = this.length - 1; current != null; i--) {
      iter(current.value, i);
      current = current.prev;
    }
  }

  map(iter: (value: T, index: number) => T): DoublyLinkedList<T> {
    if (this.length === 0) {
      return this;
    }

    let current = this.head;
    const result: DoublyLinkedList<T> = new DoublyLinkedList();
    for (let i = 0; current != null; i++) {
      result.push(iter(current.value, i));
      current = current.next;
    }

    return result;
  }

  mapReverse(iter: (value: T, index: number) => T): DoublyLinkedList<T> {
    if (this.length === 0) {
      return this;
    }

    let current = this.tail;
    const result: DoublyLinkedList<T> = new DoublyLinkedList();
    for (let i = this.length - 1; current != null; i--) {
      result.push(iter(current.value, i));
      current = current.prev;
    }

    return result;
  }

  filter(iter: (value: T, index: number) => boolean): DoublyLinkedList<T> {
    if (this.length === 0) {
      return this;
    }

    let current = this.head;
    const result: DoublyLinkedList<T> = new DoublyLinkedList();
    for (let i = 0; current != null; i++) {
      if (iter(current.value, i)) {
        result.push(current.value);
      }
      current = current.next;
    }

    return result;
  }

  _findTargetNodeByValue(value: T): NodeLocationResult<T> {
    if (this.length === 0) {
      // List is empty
      throw new Error('cannot find node: list is empty');
    }

    let target = this.head;
    for (let i = 0; target != null; i++) {
      if (target.value === value) {
        return {
          index: i,
          node: target,
        };
      }

      target = target.next;
    }

    return {
      index: -1,
      err: 'A node with that value was not found in the list.',
      node: null,
    };
  }

  _findTargetNodeByIndex(index: number): Node<T> | null {
    if (index > this.length - 1) {
      throw new ReferenceError('index out of bounds');
    }

    if (index === 0) {
      return this.head;
    }

    if (index === this.length - 1) {
      return this.tail;
    }

    if (index < this.length / 2) {
      // Index is closer to the front, start from there
      let target = this.head;
      for (let i = 0; i < index; i++) {
        if (target) {
          target = target.next;
        }
      }

      return target;
    }

    // index is closer to back so start from there
    let target = this.tail;
    for (let i = this.length - 1; i > index; i--) {
      if (target) {
        target = target.prev;
      }
    }

    return target;
  }
}
